import React from "react";
import { getSectorColor } from "../../config/colorScheme";
import { OverallEmissionsProjection } from "../dashboard/OverallEmissionsProjection";
import { CapitalExpenditureBySectorChart } from "../dashboard/CapitalExpenditureBySectorChart";
import EmissionsReductionSummary from "../dashboard/EmissionsReductionSummary";
import ScenarioInvestmentComparison from "../dashboard/ScenarioInvestmentComparison";
import { useOverallReduction } from "../../hooks/useOverallReduction";

export default function Impact({
  // required
  sectorImpact,
  calculateTotalImpact,
  scenarioKey,

  // optional-but-used
  emissionsProjection,          // { sectorReductions: { food:{percentageReduction}, ... }, reductionPercentage }
  investmentDetails,            // { annualInvestmentPerHousehold, scenario, investmentIncrease }
  from = 2025,
  to = 2050,
}) {

  return (
    <div>
      <div className="text-center mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl lg:text-3xl font-semibold mb-3">Overall Impact</h2>
        <p className="text-sm text-gray-600 mb-4 max-w-2xl mx-auto">
          Explore the total greenhouse gas emissions reduction and projected sector-by-sector impact of your selected scenario.
        </p>
      </div>

      {/* Total Impact */}
      <div className="mt-12 text-center flex flex-col items-center">
        <EmissionsReductionSummary
        scenario={scenarioKey}  // e.g. "net_zero_futures_dvl_shift_low_2208"
        from={2025}
        to={2050}
        compareYear={2025}
        />

        {/* Emissions Projection (live) */}
        <div className="w-full h-96 mt-6">
          <OverallEmissionsProjection scenario={scenarioKey}   from={2025}
            to={2050} />
        </div>
      </div>

      {/* Investment section */}
      <div className="mb-16 mt-24">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Investment needed to reach net zero by 2050
        </h3>

        <div className="bg-white p-6 rounded-lg">
          <div className="grid gap-6">
            <div>
              <h4 className="text-md font-semibold text-gray-700 mb-3">Key Investment Benefits</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg text-left">
                  <img src="/energy-money-back.jpg" alt="Lower Energy Bills & Security" className="w-full h-64 object-cover rounded-lg mb-3" />
                  <h5 className="text-sm font-medium text-blue-800 mb-2">Lower costs and energy security</h5>
                  <p className="text-xs text-gray-600">
                    Reduces household and business bills and other costs through increased efficiency, new technologies, and enabling businesses and households to do things differently (eg changing how we buy things or how we travel)
                    <br/><br/>Cuts reliance on fossil fuels, reducing UK electricity prices and increasing energy resilience with a diverse range of sources and storage.
                    <br/><br/>Cuts reliance on imported energy, stabilising supply and prices and increasing energy security.
                    <br/><br/>Offers potential for community benefits in areas close to new energy projects.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg text-left">
                  <img src="/Uk-countryside.jpg" alt="Energy Independence & Resilience" className="w-full h-64 object-cover rounded-lg mb-3" />
                  <h5 className="text-sm font-medium text-blue-800 mb-2">Economic prosperity and jobs</h5>
                  <p className="text-xs text-gray-600">
                   Creates skilled jobs across sectors, eg in renewables, home retrofits, EVs, manufacturing and construction, balancing job losses in declining industries through re-skilling.
                    <br/><br/>Boosts local manufacturing and supply chains that make low-carbon products across the UK.
                    <br/><br/>Attracts investment in research and clean tech, growing new businesses and better-paid roles.
                    <br/><br/>Builds UK expertise, leadership and exports in rapidly growing 'green economy' sectors.
                  </p>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg text-left">
                  <img src="/jobs.jpg" alt="Economic Growth & Jobs" className="w-full h-64 object-cover rounded-lg mb-3" />
                  <h5 className="text-sm font-medium text-blue-800 mb-2">Social and environmental benefits</h5>
                  <p className="text-xs text-gray-600">
                    Lowers air pollution, improving public health and reducing healthcare costs.
                    <br/><br/>Reduces waste and preserves resources through circular and sharing economy approaches.
                    <br/><br/>Creates healthier, quieter places with cleaner transport and better urban design.
                    <br/><br/>Improves nature and biodiversity in towns, cities and countryside.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Capex chart */}
          <div className="mt-12 w-full">
            <CapitalExpenditureBySectorChart
            scenario={scenarioKey}   from={2025}
            to={2050} sectors />
          </div>

          {/* Scenario investment comparison */}
         <ScenarioInvestmentComparison
            scenario="net_zero_futures_dvl_transform_low_0909"
            from={2025}
            to={2050}
            scenario={scenarioKey}
            baselineYear={2025}
            pvToGBP={1e6}
            households={1}
        />
        </div>
      </div>
    </div>
  );
}

